/**
 * 商品写真（試用・2026-10-07）— この公式値に御社の写真を付ける。承認されると kalori のカタログ（アプリ・ウェブ）の
 * 画像が、AI 生成の画像からこの写真に替わる（「事業者提供」と表示）。
 *
 * - 写真は端末で縮めてから送る（`../image.ts`・位置情報などは送らない）。サーバが中央の正方形に切り抜くので、
 *   付けたあとに出る写真が、そのまま kalori に載る形
 * - 付けるたびに「権利を持つ写真か、使う許諾を得ている」の確認が要る（規約 補遺 第5条5〜7項）
 * - 付け外しは編集中だけ（審査中・公開中は値と同じく固定）。写真だけの申請もできる
 * ⚠️ 写真は外部の AI に送らない（「写真から推定」とは別の機能）
 */
import { useState } from "react";
import { api, type MenuEntry } from "../api";
import { toJpeg } from "../image";

const TRIAL_TITLE = "パイロット中は無料です。有料化・終了は 30 日前にお知らせします。";

export function PhotoCard({ entry, editable, onChanged }: { entry: MenuEntry; editable: boolean; onChanged: () => void }) {
  const [rights, setRights] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = entry.catalogImage ?? null;
  const published = entry.revisionOf?.photo ?? null;

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (!rights) return setError("先に「権利を持つ写真か、使う許諾を得ています」にチェックを入れてください。");
    setBusy(true);
    try {
      const { base64 } = await toJpeg(file);
      const r = await api.uploadPhoto({ entryId: entry.id, imageBase64: base64, rightsConfirmed: true });
      if (!r.ok) return setError(r.error);
      setRights(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "写真を送れませんでした。");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    const r = await api.removePhoto(entry.id);
    setBusy(false);
    if (!r.ok) return setError(r.error);
    onChanged();
  };

  return (
    <div className="card">
      <h2>
        商品写真 <span className="badge trial" title={TRIAL_TITLE}>試用</span>
      </h2>
      <p className="muted small">
        承認されると、kalori のアプリとウェブサイトでこのメニューの画像が御社の写真に替わります（「事業者提供」と表示します）。写真は中央の正方形に切り抜いて載せます。写真だけでも申請できます。
      </p>
      <div className="photo-compare">
        <figure>
          {current ? <img src={current.url} alt="いま kalori に出ている画像" width={160} height={160} /> : <div className="photo-empty">画像なし</div>}
          <figcaption className="muted small">いま kalori に出ている画像{current ? (current.isAi ? "（AI 生成）" : "（御社の写真）") : ""}</figcaption>
        </figure>
        <figure>
          {entry.photo ? (
            <img src={entry.photo.url} alt="この公式値に付けた写真" width={160} height={160} />
          ) : (
            <div className="photo-empty">写真なし</div>
          )}
          <figcaption className="muted small">
            {entry.photo ? "承認されると、この写真になります" : published ? "写真を外しました（承認されると AI 生成の画像に戻ります）" : "写真を付けると、ここに載る形が出ます"}
          </figcaption>
        </figure>
      </div>
      {editable && (
        <>
          <label className="check-item">
            <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} />
            <span>
              この写真は御社が権利（著作権など）を持つものか、使う許諾を得ています。写っているのはこのメニューで、人の顔や他社のロゴは写っていません。
            </span>
          </label>
          <div className="toolbar">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic"
              aria-label={entry.photo ? "写真を替える" : "写真を選ぶ"}
              disabled={busy || !rights}
              onChange={(e) => {
                void upload(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            {entry.photo && (
              <button type="button" className="btn-link small" disabled={busy} onClick={() => void remove()}>
                写真を外す
              </button>
            )}
            {busy && <span className="muted small">処理しています…</span>}
          </div>
        </>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
